// Layer 2 (Engine): session-local advisory mutations — approve recommended
// actions, answer evidence, ack alarms, and gate FMEA changes. Seed data stays
// frozen; state is derived.

import type {
  ActionDecision,
  Alarm,
  Case,
  ControlModeCheck,
  EvidenceRequest,
  FMEAUpdateReview,
  RecommendedAction,
  SampleRequest,
} from '@/versions/v1/schema/case';
import type { ControlMode } from '@/versions/v1/schema/advisory-issue';
import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import type { SeatId } from '@/versions/v1/schema/seat';
import type { Ruling } from '@/versions/v1/schema/claim';
import { FMEA_UPDATE_REVIEWS, REPORT_CASE_PINS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { liveRuleSet } from '@/versions/v1/shared/RuleSetEngine';
import { resolveHandler } from '@/versions/v1/shared/org';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import {
  CONTROL_PLAN_ZN_ROWS,
  type ControlPlanRow,
} from '@/versions/v1/shared/bunker-hill-control-plan-seed';

export type AdvisorySessionState = {
  /**
   * Decisions made this session, keyed by recommended-action id — approve
   * (`reason` absent) or refuse (`reason` required), route-reads
   * `paul-feedback.A8` hard rule 1. Replaces the pre-#268 `approvedActionIds`
   * / `approvedActionAt` / `approvedActionBy` triple.
   */
  actionDecisions: ReadonlyMap<string, ActionDecision>;
  /** When a decided (approved) action was actually implemented on the plant — hard rule 3, never the decision stamp. */
  implementedActionAt: ReadonlyMap<string, SeriesTime>;
  answeredEvidence: ReadonlyMap<string, { answer: string; answeredAt: string }>;
  ackedAlarmIds: ReadonlySet<string>;
  reviewedChanges: ReadonlyMap<string, { notes: string; reviewedAt: string }>;
  approvedChangeIds: ReadonlySet<string>;
  rejectedChanges: ReadonlyMap<string, { reason: string; rejectedAt: string }>;
  rulings: readonly Ruling[];
  /** route-reads paul-call.A4 #253 -- keyed by InBandDivergence.id (S1.4). */
  sampleRequests: ReadonlyMap<string, SampleRequest>;
  /** Session-approved control-plan rows (band-change reviews). Reload resets. */
  controlPlanSessionRows: readonly ControlPlanRow[];
  /** Session overrides for daily-report tracked-issue pins (paul-tarp.A19). */
  reportUnpinnedCaseIds: ReadonlySet<string>;
  reportPinnedCaseIds: ReadonlySet<string>;
};

export const INITIAL_ADVISORY_STATE: AdvisorySessionState = {
  actionDecisions: new Map(),
  implementedActionAt: new Map(),
  answeredEvidence: new Map(),
  ackedAlarmIds: new Set(),
  reviewedChanges: new Map(),
  approvedChangeIds: new Set(),
  rejectedChanges: new Map(),
  rulings: [],
  sampleRequests: new Map(),
  controlPlanSessionRows: [],
  reportUnpinnedCaseIds: new Set(),
  reportPinnedCaseIds: new Set(),
};

type RulingContext = {
  cursor?: string;
  authorRole?: PlantRoleId;
};

function rulingForReview(
  reviewId: string,
  notes: string,
  decidedAt: string,
  authorRole: PlantRoleId,
): Ruling | undefined {
  const review = FMEA_UPDATE_REVIEWS.find((row) => row.id === reviewId);
  if (!review) return undefined;
  if (review.proposedChange.kind === 'band-change') {
    const rowId = review.proposedChange.bandChange?.controlPlanRowId;
    if (!rowId) return undefined;
    return {
      id: `session-ruling-${reviewId}-review-${decidedAt}`,
      subject: { kind: 'band', id: rowId },
      acceptsClaimId: `claim-band-${rowId}-proposed`,
      author: { role: authorRole },
      decidedAt,
      reason: notes,
      stage: 'review',
    };
  }
  const ruleId = review.proposedChange.ruleId;
  if (!ruleId) return undefined;
  return {
    id: `session-ruling-${reviewId}-review-${decidedAt}`,
    subject: { kind: 'rule', id: ruleId },
    acceptsClaimId: `claim-rule-${ruleId}-case`,
    author: { role: authorRole },
    decidedAt,
    reason: notes,
    stage: 'review',
  };
}

function rulingForApproval(
  reviewId: string,
  decidedAt: string,
  authorRole: PlantRoleId,
): Ruling | undefined {
  const review = FMEA_UPDATE_REVIEWS.find((row) => row.id === reviewId);
  if (!review) return undefined;
  if (review.proposedChange.kind === 'band-change') {
    const rowId = review.proposedChange.bandChange?.controlPlanRowId;
    if (!rowId) return undefined;
    return {
      id: `session-ruling-${reviewId}-approval-${decidedAt}`,
      subject: { kind: 'band', id: rowId },
      acceptsClaimId: `claim-band-${rowId}-proposed`,
      author: { role: authorRole },
      decidedAt,
      reason: review.proposedChange.summary,
      stage: 'approval',
    };
  }
  const ruleId = review.proposedChange.ruleId;
  if (!ruleId) return undefined;
  const claimId =
    ruleId === 'dr-dart-valve' ? 'claim-rule-dr-dart-valve-narrow' : `claim-rule-${ruleId}-case`;
  return {
    id: `session-ruling-${reviewId}-approval-${decidedAt}`,
    subject: { kind: 'rule', id: ruleId },
    acceptsClaimId: claimId,
    author: { role: authorRole },
    decidedAt,
    reason: review.proposedChange.summary,
    stage: 'approval',
  };
}

export function approveAction(
  state: AdvisorySessionState,
  actionId: string,
  approvedAt: SeriesTime,
  seat: SeatId,
): AdvisorySessionState {
  if (state.actionDecisions.has(actionId)) return state;
  const actionDecisions = new Map(state.actionDecisions);
  actionDecisions.set(actionId, { seat, at: approvedAt });
  return { ...state, actionDecisions };
}

/** A refusal — the same event as approval, with a reason (hard rule 1). Required, never a shrug. */
export function rejectAction(
  state: AdvisorySessionState,
  actionId: string,
  rejectedAt: SeriesTime,
  seat: SeatId,
  reason: string,
): AdvisorySessionState {
  const actionDecisions = new Map(state.actionDecisions);
  actionDecisions.set(actionId, { seat, at: rejectedAt, reason });
  return { ...state, actionDecisions };
}

/** Implementation — a third time, never the decision time (hard rule 3). Only meaningful on an approved action. */
export function implementAction(
  state: AdvisorySessionState,
  actionId: string,
  implementedAt: SeriesTime,
): AdvisorySessionState {
  const implementedActionAt = new Map(state.implementedActionAt);
  implementedActionAt.set(actionId, implementedAt);
  return { ...state, implementedActionAt };
}

export function answerEvidence(
  state: AdvisorySessionState,
  evidenceId: string,
  answer: string,
  answeredAt = 'now',
): AdvisorySessionState {
  const answeredEvidence = new Map(state.answeredEvidence);
  answeredEvidence.set(evidenceId, { answer, answeredAt });
  return { ...state, answeredEvidence };
}

/**
 * Projection: merge session decisions / implementations onto seeded actions.
 * `status` is derived, never stored redundantly: a session refusal wins over
 * a seeded decision (a person can still change their mind on a proposed
 * action), a session decision overrides a seeded one, and `implementedAt`
 * (session or seed) only takes effect on an approved decision — hard rule 4,
 * `done` is only reachable with an implementation stamp.
 */
export function applyRecommendedActions(
  actions: readonly RecommendedAction[],
  state: AdvisorySessionState,
): RecommendedAction[] {
  if (state.actionDecisions.size === 0 && state.implementedActionAt.size === 0) {
    return actions.slice();
  }
  return actions.map((action) => {
    const decision = state.actionDecisions.get(action.id) ?? action.decision;
    const implementedAt = state.implementedActionAt.get(action.id) ?? action.implementedAt;
    if (!decision) return action;
    // Base fields only — never spread a union-typed `action` into a new
    // object literal that also sets `status`, or the discriminant and its
    // dependent fields (schema/case.ts MUST-FIX 7) stop agreeing with each
    // other under TypeScript's structural narrowing.
    const base = {
      id: action.id,
      caseId: action.caseId,
      sequence: action.sequence,
      urgency: action.urgency,
      text: action.text,
    };
    if (decision.reason) {
      // A refusal — the same event as approval, with a reason (slice 2 hard
      // rule 1) — never carries an implementation stamp.
      return { ...base, status: 'rejected' as const, decision: decision as ActionDecision & { reason: string } };
    }
    if (implementedAt) {
      return { ...base, status: 'done' as const, decision, implementedAt };
    }
    return { ...base, status: 'approved' as const, decision };
  });
}

/** Loop tag embedded in loop-state evidence copy (AIC5002, LIC4010, …). */
function loopTagFromEvidenceText(text: string): string | undefined {
  const match = text.match(/\b(AIC|LIC|WIC|JIC|FIT)\d+[A-Z]?\b/i);
  return match?.[0]?.toUpperCase();
}

function controlModeFromLoopAnswer(answer: string): ControlMode | undefined {
  const normalized = answer.toUpperCase();
  if (normalized === 'AUTO') return 'auto';
  if (normalized === 'MANUAL') return 'manual';
  return undefined;
}

function controlModeCheckFromLoopAnswer(
  request: EvidenceRequest,
  answer: string,
  answeredAt: string,
): ControlModeCheck | undefined {
  if (request.kind !== 'loop-state') return undefined;
  const mode = controlModeFromLoopAnswer(answer);
  const tag = loopTagFromEvidenceText(request.text);
  if (!mode || !tag) return undefined;
  return { tag, mode, confirmedBy: 'cro', confirmedAt: answeredAt };
}

/** Projection: merge session answers onto a case's evidence requests. */
export function applyEvidenceToCase(
  caseItem: Case,
  state: AdvisorySessionState,
): Case {
  if (state.answeredEvidence.size === 0 || caseItem.evidenceRequests.length === 0) {
    return caseItem;
  }
  let controlModeCheck = caseItem.controlModeCheck;
  const evidenceRequests = caseItem.evidenceRequests.map((request) => {
    const answered = state.answeredEvidence.get(request.id);
    if (!answered) return request;
    const check = controlModeCheckFromLoopAnswer(request, answered.answer, answered.answeredAt);
    if (check) controlModeCheck = check;
    return {
      ...request,
      status: 'answered' as const,
      answer: answered.answer,
      answeredAt: answered.answeredAt,
    };
  });
  return { ...caseItem, evidenceRequests, controlModeCheck };
}

export function ackAlarm(
  state: AdvisorySessionState,
  alarmId: string,
): AdvisorySessionState {
  if (state.ackedAlarmIds.has(alarmId)) return state;
  const ackedAlarmIds = new Set(state.ackedAlarmIds);
  ackedAlarmIds.add(alarmId);
  return { ...state, ackedAlarmIds };
}

/** Projection: merge session acks onto seeded alarms. */
export function applyAlarms(
  alarms: readonly Alarm[],
  state: AdvisorySessionState,
): Alarm[] {
  if (state.ackedAlarmIds.size === 0) return alarms.slice();
  return alarms.map((alarm) =>
    state.ackedAlarmIds.has(alarm.id) && alarm.state === 'active'
      ? { ...alarm, state: 'acked' }
      : alarm,
  );
}

export function reviewChange(
  state: AdvisorySessionState,
  reviewId: string,
  notes: string,
  reviewedAt = 'now',
  context: RulingContext = {},
): AdvisorySessionState {
  const reviewedChanges = new Map(state.reviewedChanges);
  reviewedChanges.set(reviewId, { notes, reviewedAt });
  const ruling = rulingForReview(
    reviewId,
    notes,
    context.cursor ?? reviewedAt,
    context.authorRole ?? 'metallurgist',
  );
  const rulings = ruling ? [...state.rulings, ruling] : state.rulings;
  return { ...state, reviewedChanges, rulings };
}

export function approveChange(
  state: AdvisorySessionState,
  reviewId: string,
  context: RulingContext = {},
): AdvisorySessionState {
  if (state.approvedChangeIds.has(reviewId)) return state;
  const approvedChangeIds = new Set(state.approvedChangeIds);
  approvedChangeIds.add(reviewId);
  const decidedAt = context.cursor ?? 'now';
  const ruling = rulingForApproval(reviewId, decidedAt, context.authorRole ?? 'gm');
  const rulings = ruling ? [...state.rulings, ruling] : state.rulings;
  const review = FMEA_UPDATE_REVIEWS.find((row) => row.id === reviewId);
  let controlPlanSessionRows = state.controlPlanSessionRows;
  if (review?.proposedChange.kind === 'band-change' && review.proposedChange.bandChange) {
    const { controlPlanRowId, toZoneId, proposedBand } = review.proposedChange.bandChange;
    const base = CONTROL_PLAN_ZN_ROWS.find((row) => row.id === controlPlanRowId);
    if (base) {
      const nextRow: ControlPlanRow = {
        ...base,
        id: `${controlPlanRowId}-${toZoneId}`,
        zoneId: toZoneId,
        specOverlay: proposedBand,
      };
      controlPlanSessionRows = [...controlPlanSessionRows, nextRow];
    }
  }
  return { ...state, approvedChangeIds, rulings, controlPlanSessionRows };
}

export function rejectChange(
  state: AdvisorySessionState,
  reviewId: string,
  reason: string,
  rejectedAt = 'now',
): AdvisorySessionState {
  const rejectedChanges = new Map(state.rejectedChanges);
  rejectedChanges.set(reviewId, { reason, rejectedAt });
  return { ...state, rejectedChanges };
}

/** Who a session review is recorded against: the holder of the Senior
 *  metallurgist seat for a review routed there (L. Chen, org seed), else the
 *  Metallurgist who takes the other seats' reviews (P27 r1, row 317). */
function sessionReviewerName(review: FMEAUpdateReview, at: string): string {
  if (review.reviewSeats.includes('senior-metallurgist')) {
    return resolveHandler('senior-metallurgist', at)?.name ?? 'S. Park';
  }
  return 'S. Park';
}

/** Projection: merge session review / approve / reject onto seeded reviews. */
export function applyFmeaReviews(
  reviews: readonly FMEAUpdateReview[],
  state: AdvisorySessionState,
): FMEAUpdateReview[] {
  if (
    state.reviewedChanges.size === 0 &&
    state.approvedChangeIds.size === 0 &&
    state.rejectedChanges.size === 0
  ) {
    return reviews.slice();
  }
  return reviews.map((review) => {
    const rejected = state.rejectedChanges.get(review.id);
    if (rejected) {
      return {
        ...review,
        reviewStatus: 'rejected' as const,
        approvalStatus: 'rejected' as const,
        reviewNotes: rejected.reason,
      };
    }
    const approved = state.approvedChangeIds.has(review.id);
    const reviewed = state.reviewedChanges.get(review.id);
    let next = review;
    if (reviewed) {
      next = {
        ...next,
        reviewStatus: 'approved',
        reviewedBy: sessionReviewerName(review, reviewed.reviewedAt),
        reviewedAt: reviewed.reviewedAt,
        reviewNotes: reviewed.notes,
      };
    }
    if (approved) {
      const sessionApproval = state.rulings.find(
        (row) => row.stage === 'approval' && row.id.includes(review.id),
      );
      const approvedByLabel =
        sessionApproval?.author.name ??
        (sessionApproval?.author.role === 'gm' ? 'Plant Manager' : sessionApproval?.author.role);
      next = {
        ...next,
        reviewStatus: next.reviewStatus === 'pending' ? 'approved' : next.reviewStatus,
        approvalStatus: 'approved',
        approvedBy: approvedByLabel ?? next.approvedBy,
        approvedAt: sessionApproval?.decidedAt ?? next.approvedAt,
      };
    }
    return next;
  });
}

/** Projection: a rule is live when the resolved rule set includes it. */
export function isRuleLive(ruleId: string, advisory: AdvisorySessionState): boolean {
  return liveRuleSet(advisory).rules.some((row) => row.id === ruleId);
}

export function requestSamples(
  state: AdvisorySessionState,
  divergenceId: string,
  requestedAt: SeriesTime,
  cadence: 'lab' | 'osa',
  samplePointId?: string,
  requestedBy: PlantRoleId = 'cro',
): AdvisorySessionState {
  const sampleRequests = new Map(state.sampleRequests);
  // `mode`/`campaign` record what was asked at click time -- the projection
  // that decides what renders reads the *current* `state.cadence` instead
  // (SA-F3 fix round 1), so this is provenance only, never consulted to
  // decide what the CRO or Met sees.
  const request: SampleRequest =
    cadence === 'osa'
      ? {
          divergenceId,
          samplePointId,
          requestedAt,
          requestedBy,
          mode: 'campaign',
          campaign: { intervalMin: 30, hours: 4 },
        }
      : { divergenceId, samplePointId, requestedAt, requestedBy, mode: 'rush' };
  sampleRequests.set(divergenceId, request);
  return { ...state, sampleRequests };
}

export function effectiveReportPinnedCaseIds(state: AdvisorySessionState): readonly string[] {
  const pinned = new Set(REPORT_CASE_PINS.map((row) => row.caseId));
  for (const caseId of state.reportPinnedCaseIds) pinned.add(caseId);
  for (const caseId of state.reportUnpinnedCaseIds) pinned.delete(caseId);
  return [...pinned];
}

export function isCasePinnedForReport(state: AdvisorySessionState, caseId: string): boolean {
  return effectiveReportPinnedCaseIds(state).includes(caseId);
}

export function pinCaseForReport(state: AdvisorySessionState, caseId: string): AdvisorySessionState {
  const reportUnpinnedCaseIds = new Set(state.reportUnpinnedCaseIds);
  reportUnpinnedCaseIds.delete(caseId);
  const reportPinnedCaseIds = new Set(state.reportPinnedCaseIds);
  reportPinnedCaseIds.add(caseId);
  return { ...state, reportUnpinnedCaseIds, reportPinnedCaseIds };
}

export function unpinCaseFromReport(state: AdvisorySessionState, caseId: string): AdvisorySessionState {
  if (REPORT_CASE_PINS.some((row) => row.caseId === caseId)) {
    const reportUnpinnedCaseIds = new Set(state.reportUnpinnedCaseIds);
    reportUnpinnedCaseIds.add(caseId);
    return { ...state, reportUnpinnedCaseIds };
  }
  const reportPinnedCaseIds = new Set(state.reportPinnedCaseIds);
  reportPinnedCaseIds.delete(caseId);
  return { ...state, reportPinnedCaseIds };
}
